import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";
import { addDays, format } from "date-fns";
import { DefaultTooltipContent } from "recharts";

import fpResrcWipProfileService from "@plannel/services/fp/fp-resrc-wip-profile-service";
import numberUtil from "@plannel/utils/number-util";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup, { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import FpGridSetting, {
  readHide, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, buildDayBucketPivotColumnDefs, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap } from "@plannel/pages/factory-plan/common/chart";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — planresult/PlanResult 파일럿에서 확인된 값(9)을
// 그대로 재사용한다(테넌트에 저장된 단일 값이라 화면별로 분기되지 않음, fp-report-screen-guide.md §A-3c).
// old ResrcWipProfile.jsx의 로컬 fallback(14, DB 로드 전 임시값)이 아니라 실제 DB 값을 이식한다.
// TODO(MIG-T2): [resrcwipprofile/ResrcWipProfile] 조회조건 세팅 3종(버킷 목록/검색필드 표시/검색구간)의
//   테넌트별 커스터마이징 미이관.
//   old: search-option/bucket · search-option/srh-options · setting FP_SRH_RANGE를 각각 DB에서 조회
//   사유: 정적 구성(JSON 카탈로그/고정 카탈로그/상수)으로 전환 — 가이드 §A-3~§A-3d에서 결정
const SEARCH_ZONE_DAYS = 9;

// old ResrcWipProfile.jsx는 <SearchOptsGroup ref={searchOptGroupRef} onKeyDown={onParamKeyPress} />를
// 렌더링하지만 loadData/loadChartData 어디서도 searchOptGroupRef.current.getValues()를 호출하지
// 않는다 — 즉 old에서도 이 조회조건은 실제로 아무 값도 서버로 전달되지 않는 죽은 UI였다
// (PlanResult/ResourceUtilization과 달리 이 화면만 그렇다 — 화면마다 old 소스를 직접 확인해야 하는
// 이유). 동작하지 않던 필드를 이관하면 오히려 "입력해도 반영 안 되는" 혼란을 새로 만들 뿐이라
// SearchOptsGroupRetrieve는 이 화면에 추가하지 않는다(빠뜨린 기능이 아니라 old에도 없던 기능).
const PARAM_DATE_FORMAT = "yyyyMMdd";

// old ResrcWipProfileChart.jsx도 다른 FP 차트들과 동일하게 getDefaultChartOptions
// (useAbsoluteValue=true)를 썼다(재공수량은 실제로 음수가 나올 일이 없어 오늘 당장은 시각적으로
// 차이가 없지만, old 관례와 다른 화면들의 일관성을 맞춘다 — fp-report-screen-guide.md §A-7).
// PivotChartComposed(공용)의 기본 툴팁 포맷터는 음수면 "-"를 무조건 다시 붙이는 로직이라
// 화면별로 끌 수 없어 customTooltip으로 recharts 기본 툴팁(DefaultTooltipContent)을 재사용하면서
// formatter만 교체한다(ProdSalesStockStatus.js와 동일 패턴).
const AbsValueTooltip = (props) => (
  <DefaultTooltipContent {...props} formatter={(value) => numberUtil.formatNumber(Math.abs(value))} />
);

// 트리(행 그룹) 계층 — 공장 > 스테이지 > 자원 > 고객 > 공정그룹 > (leaf)공정. old 컬럼정의
// (plantGroup/stageGroup/resourceGroup/customerGroup/routeGroup 전부 `mergeRule`)를 근거로 Row
// Grouping을 선택했다(Tree Data가 아님, fp-report-screen-guide.md §A-6b). old의 routeGroup은
// routeGrpCd/Nm+routeCd/Nm 4개를 한 컬럼그룹으로 묶었지만, 데이터 그레인(grain) 자체가 6개 식별자
// 전체 조합 단위(ResrcWipProfileService#getResrcWipProfiles의 groupColumns)라 routeGrp까지는
// 그룹(행 트리) 레벨이고 route(공정)가 실제 leaf(각 행을 구분하는 마지막 값)다 — PlanResult의
// itemCd와 같은 역할.
const GROUP_FIELDS = [
  "plantCd", "plantNm", "stageCd", "stageNm", "resourceCd", "resourceNm", "customerCd", "customerNm",
  "routeGrpCd", "routeGrpNm"
];

// 드릴다운(클릭 대상) 판단과 차트 groupKey 산정에 공통으로 쓰는 계층 순서(코드 필드만).
const HIERARCHY_FIELDS = ["plantCd", "stageCd", "resourceCd", "customerCd", "routeGrpCd", "routeCd"];

const PLANT_CD_COL_ID = "plantCdGrp";
const PLANT_NM_COL_ID = `PLANT_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const STAGE_CD_COL_ID = "stageCdGrp";
const STAGE_NM_COL_ID = `STAGE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const RESOURCE_CD_COL_ID = "resourceCdGrp";
const RESOURCE_NM_COL_ID = `RESOURCE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const CUSTOMER_CD_COL_ID = "customerCdGrp";
const CUSTOMER_NM_COL_ID = `CUSTOMER_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ROUTE_GRP_CD_COL_ID = "routeGrpCdGrp";
const ROUTE_GRP_NM_COL_ID = `ROUTE_GRP_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ROUTE_NM_COL_ID = `ROUTE_NM${GridUtils.IGNORE_P13N_COLLID}`;

const PLANT_NM_HIDE_KEY = "plantNmColsHide";
const STAGE_NM_HIDE_KEY = "stageNmColsHide";
const RESOURCE_NM_HIDE_KEY = "resourceNmColsHide";
const CUSTOMER_NM_HIDE_KEY = "customerNmColsHide";
const ROUTE_GRP_NM_HIDE_KEY = "routeGrpNmColsHide";
const ROUTE_NM_HIDE_KEY = "routeNmColsHide";

const ResrcWipProfile = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const [chartData, setChartData] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);
  // old는 드릴다운 레벨과 무관하게 항상 "작업 대기 수량"(FP_WIP_QTY) 고정 레전드 1개만 보여준다
  // (클릭한 값별로 레전드가 늘어나지 않음) — pivotConfig.groupKey로 쓸 고정 필드명.
  const CHART_SERIES_KEY = "series";

  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  const prevChartParamsRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const searchStateRef = useRef({ versionState, periodState });
  searchStateRef.current = { versionState, periodState };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveResrcWipProfile(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 고정(왼쪽 pinned) 컬럼 — old fixColumns(plantCd/plantNm/stageCd/stageNm/resourceCd/resourceNm/
  // customerCd/customerNm/routeGrpCd/routeGrpNm/routeCd/routeNm)와 1:1. 공장/스테이지/자원/고객/
  // 공정그룹은 행 그룹(트리), 공정(route)은 leaf라 평범한 field 컬럼 쌍이다(코드↔명칭 토글은
  // 다른 레벨과 동일하게 CustomDetailHeader로 통일 — old는 routeCd/routeNm 둘 다 always 표시라
  // 토글이 없었지만, 이 코드베이스 Report 전 화면의 코드/명칭 토글 컨벤션에 맞춘다). 이 화면은
  // SUM 행 개념이 없어 valueFormatter는 넘기지 않는다(common/grid.js buildGroupColumnPair).
  const fixedColumnDefs = useMemo(() => [
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),

    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, PLANT_NM_HIDE_KEY),
      cdColId: PLANT_CD_COL_ID, cdField: "plantCd", cdHeader: "FP_PLANT_CD",
      nmColId: PLANT_NM_COL_ID, nmField: "plantNm", nmHeader: "PLANT_NM",
      hideKey: PLANT_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, STAGE_NM_HIDE_KEY),
      cdColId: STAGE_CD_COL_ID, cdField: "stageCd", cdHeader: "FP_STAGE_CD",
      nmColId: STAGE_NM_COL_ID, nmField: "stageNm", nmHeader: "FP_STAGE_NM",
      hideKey: STAGE_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, RESOURCE_NM_HIDE_KEY),
      cdColId: RESOURCE_CD_COL_ID, cdField: "resourceCd", cdHeader: "FP_RESOURCE_CD",
      nmColId: RESOURCE_NM_COL_ID, nmField: "resourceNm", nmHeader: "FP_RESOURCE_NM",
      hideKey: RESOURCE_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, CUSTOMER_NM_HIDE_KEY),
      cdColId: CUSTOMER_CD_COL_ID, cdField: "customerCd", cdHeader: "FP_CUSTOMER_CD",
      nmColId: CUSTOMER_NM_COL_ID, nmField: "customerNm", nmHeader: "FP_CUSTOMER_NM",
      hideKey: CUSTOMER_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, ROUTE_GRP_NM_HIDE_KEY),
      cdColId: ROUTE_GRP_CD_COL_ID, cdField: "routeGrpCd", cdHeader: "FP_ROUTE_GRP_CD",
      nmColId: ROUTE_GRP_NM_COL_ID, nmField: "routeGrpNm", nmHeader: "FP_ROUTE_GRP_NM",
      hideKey: ROUTE_GRP_NM_HIDE_KEY, width: 150
    }),

    // leaf(공정) — 그룹이 아니라 실제 행의 값. 코드/명칭 토글은 위 레벨들과 동일한 방식.
    {
      headerName: "FP_ROUTE_CD", field: "routeCd", colId: "routeCd", width: 150, pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"],
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide: readHide(reduxViewState, ROUTE_NM_HIDE_KEY), reduxKey: ROUTE_NM_HIDE_KEY, detailColumns: [ROUTE_NM_COL_ID]
      }
    },
    {
      headerName: "FP_ROUTE_NM", field: "routeNm", colId: ROUTE_NM_COL_ID, width: 150, pinned: "left", lockPinned: true,
      hide: readHide(reduxViewState, ROUTE_NM_HIDE_KEY), type: ["nonEditableColumn"]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // 일버킷일 때 자식 헤더로 오는 요일 코드 — old setColumnColorOnSunSat 대응(§A-6f), 다국어+토/일
  // 강조색까지 common/grid.js의 buildDayBucketPivotColumnDefs가 처리한다.
  const buildPivotColumnDefs = (crosstabHeader) => buildDayBucketPivotColumnDefs(crosstabHeader, t);

  // old setCrossTabGridDataWithBucket — pivotData[i]를 crosstabHeader[i] 필드로 펼친다.
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {
      plantCd: row.plantCd, plantNm: row.plantNm,
      stageCd: row.stageCd, stageNm: row.stageNm,
      resourceCd: row.resourceCd, resourceNm: row.resourceNm,
      customerCd: row.customerCd, customerNm: row.customerNm,
      routeGrpCd: row.routeGrpCd, routeGrpNm: row.routeGrpNm,
      routeCd: row.routeCd, routeNm: row.routeNm
    };
    (crosstabHeader ?? []).forEach((header, index) => {
      flat[header] = row.pivotData?.[index];
    });
    return flat;
  });

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const buildBaseParams = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      bucketType: period.bucketType,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartData([]);
    setChartSeries([]);
    setChartLabels([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 브레드크럼 — FilterStatusPanel의 6개 슬롯(originLoc/destLoc/customer/item/
  // workCenter/resource)이 정확히 이 화면의 계층 depth(6)와 맞아떨어진다. 슬롯 이름은 RP/DP
  // 도메인 어휘라 이 화면의 공장>스테이지>자원>고객>공정그룹>공정 순서와 의미상 안 맞지만,
  // 라벨은 화면이 *Header 값으로 직접 넣으므로 순서만 계층과 일치시키면 된다(PlanResult.js와
  // 동일한 슬롯 재활용 원칙, fp-report-screen-guide.md §A-7).
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      originLocCd: target.plantCd ?? null, originLocCdHeader: t("FP_PLANT_CD"),
      originLocNm: target.plantNm ?? null, originLocNmHeader: t("PLANT_NM"),
      destLocCd: target.stageCd ?? null, destLocCdHeader: t("FP_STAGE_CD"),
      destLocNm: target.stageNm ?? null, destLocNmHeader: t("FP_STAGE_NM"),
      customerCd: target.resourceCd ?? null, customerCdHeader: t("FP_RESOURCE_CD"),
      customerNm: target.resourceNm ?? null, customerNmHeader: t("FP_RESOURCE_NM"),
      itemCd: target.customerCd ?? null, itemCdHeader: t("FP_CUSTOMER_CD"),
      itemNm: target.customerNm ?? null, itemNmHeader: t("FP_CUSTOMER_NM"),
      workCenterCd: target.routeGrpCd ?? null, workCenterCdHeader: t("FP_ROUTE_GRP_CD"),
      workCenterNm: target.routeGrpNm ?? null, workCenterNmHeader: t("FP_ROUTE_GRP_NM"),
      resourceCd: target.routeCd ?? null, resourceCdHeader: t("FP_ROUTE_CD"),
      resourceNm: target.routeNm ?? null, resourceNmHeader: t("FP_ROUTE_NM")
    });
  };

  // 클릭 기준으로 차트를 다시 조회한다. target = 클릭 레벨까지의 식별자(상위 레벨을 클릭하면
  // 하위 코드는 없음 — old updateChartFromGrid와 동일).
  const loadChart = (target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;

    updateStatusBreadcrumb(target);

    const nextParams = {};
    HIERARCHY_FIELDS.forEach(field => { nextParams[field] = target[field] ?? ""; });
    const prev = prevChartParamsRef.current;
    if (prev && HIERARCHY_FIELDS.every(field => prev[field] === nextParams[field])) return;
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    // old loadChartData는 useGrouping:true(그리드 조회의 false와 반대) — 드릴다운 레벨로 백엔드가
    // 집계해서 내려준다.
    const params = { ...buildBaseParams(), ...nextParams, useGrouping: true };

    fpResrcWipProfileService.getResrcWipProfileChart(params)
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { chartHeader, data } = response.data ?? {};
        const rows = data ?? [];

        if (isEmpty(rows) || isEmpty(chartHeader)) {
          setChartData([]);
          setChartSeries([]);
          setChartLabels([]);
          return;
        }

        // old는 클릭 레벨과 무관하게 항상 "작업 대기 수량"(FP_WIP_QTY) 고정 레전드 1개로 보여준다
        // (row.datasets.flatMap(... label: transLangKey('FP_WIP_QTY') ...) — 클릭한 값별로
        // 레전드가 늘어나지 않는다). useGrouping:true로 이미 클릭 레벨까지 집계된 행이 오지만,
        // 방어적으로 여러 행이 와도 버킷별 합산으로 고정 1계열만 만든다.
        const wipQtySum = chartHeader.map(() => 0);
        rows.forEach(row => {
          chartHeader.forEach((_, index) => { wipQtySum[index] += row.pivotData?.[index] ?? 0; });
        });
        const wipQtyLabel = t("FP_WIP_QTY");
        const point = { [CHART_SERIES_KEY]: wipQtyLabel };
        chartHeader.forEach((header, index) => { point[header] = wipQtySum[index]; });

        setChartLabels(chartHeader);
        setChartData([point]);
        setChartSeries([{
          field: wipQtyLabel, text: wipQtyLabel, type: "bar", groupName: t("FP_WIP_QTY"),
          typeOptions: { stackId: "resrcWipProfile" }
        }]);
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrieveResrcWipProfile = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    const params = { ...buildBaseParams(), plantCds: version.plantCd, useGrouping: false };

    gridRef.current?.api?.setGridOption("loading", true);

    fpResrcWipProfileService.getResrcWipProfiles(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        // old: 조회 직후 첫 행의 공장 기준으로 차트를 그리고, 그리드의 선택 셀도 그 행으로 옮긴다.
        pendingFocusRef.current = { rowIndex: 0, colId: PLANT_CD_COL_ID };
        prevChartParamsRef.current = null;
        loadChart({ plantCd: rows[0].plantCd, plantNm: rows[0].plantNm });
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  const PLANT_LEVEL_COL_IDS = new Set([PLANT_CD_COL_ID, PLANT_NM_COL_ID]);
  const STAGE_LEVEL_COL_IDS = new Set([STAGE_CD_COL_ID, STAGE_NM_COL_ID]);
  const RESOURCE_LEVEL_COL_IDS = new Set([RESOURCE_CD_COL_ID, RESOURCE_NM_COL_ID]);
  const CUSTOMER_LEVEL_COL_IDS = new Set([CUSTOMER_CD_COL_ID, CUSTOMER_NM_COL_ID]);
  const ROUTE_GRP_LEVEL_COL_IDS = new Set([ROUTE_GRP_CD_COL_ID, ROUTE_GRP_NM_COL_ID]);

  // 클릭한 셀에서 차트를 그릴 드릴다운 대상을 뽑는다(old updateChartFromGrid의
  // `column.startsWith(...)` 분기를 트리 표시 컬럼 colId 기준으로 옮긴 것). groupHideOpenParents
  // 때문에 그룹 행(접힌 대표 행)과 leaf(공정) 행 둘 다 다뤄야 한다(fp-report-screen-guide.md §A-6b).
  const resolveChartTarget = (params) => {
    const node = params.node;
    if (!node) return null;

    if (node.group) {
      const target = {};
      for (let current = node; current; current = current.parent) {
        if (current.field) target[current.field] = current.key;
      }
      return isEmpty(target) ? null : target;
    }

    const row = node.data;
    if (!row) return null;

    const colId = params.column?.getColId() ?? "";
    if (PLANT_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm };
    }
    if (STAGE_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm };
    }
    if (RESOURCE_LEVEL_COL_IDS.has(colId)) {
      return {
        plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm,
        resourceCd: row.resourceCd, resourceNm: row.resourceNm
      };
    }
    if (CUSTOMER_LEVEL_COL_IDS.has(colId)) {
      return {
        plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm,
        resourceCd: row.resourceCd, resourceNm: row.resourceNm, customerCd: row.customerCd, customerNm: row.customerNm
      };
    }
    if (ROUTE_GRP_LEVEL_COL_IDS.has(colId)) {
      return {
        plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm,
        resourceCd: row.resourceCd, resourceNm: row.resourceNm, customerCd: row.customerCd, customerNm: row.customerNm,
        routeGrpCd: row.routeGrpCd, routeGrpNm: row.routeGrpNm
      };
    }
    return row;
  };

  const onCellClicked = (params) => {
    const target = resolveChartTarget(params);
    if (target) loadChart(target);
  };

  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  // 버전이 바뀌면 조회 구간을 다시 계산한다(old setVersionPeriod) — PlanResult.js/
  // ResourceUtilization.js와 동일 원리.
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = addDays(startDate, SEARCH_ZONE_DAYS);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 선택형 조건(버전/plant/기간/버킷)이 바뀌면 값이 준비되는 즉시 자동 조회한다(§A-9b) — 이 화면엔
  // 자유 텍스트 조회조건이 없어(위 SearchOptsGroupRetrieve 관련 주석 참고) 이 effect 하나로 충분하다.
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodState.startDate, periodState.endDate, periodState.bucketType, periodState.isEntirePlanPeriod,
    versionState.plantCd
  ]);

  // x축(주+월 등 버킷이 섞이면) 기본 사전순 정렬을 chartLabels 원래 순서로 덮어쓴다
  // (common/chart.js, fp-report-screen-guide.md §A-7 — 크로스탭 차트 화면 공통).
  const pivotConfig = useMemo(() => ({
    groupKey: CHART_SERIES_KEY,
    chartSeries,
    include: chartLabels,
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [chartSeries, chartLabels]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          // Report 화면 bucketCase 배정표(fp-report-screen-guide.md §A-3) — ResProdPlan/
          // ResrcUtilization 2개 화면만 WITHOUT_HOUR, 이 화면은 그 예외에 없으므로 WITH_HOUR.
          bucketCase={BUCKET_CASE.WITHOUT_HOUR}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-resrc-wip-profile-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* TODO(MIG-T2): [resrcwipprofile/ResrcWipProfile] 차트 장식성 인터랙션 3종 미이관.
                old: ChartHatchPatternPlugin(빗금), highlightChartByLabel/resetChartHighlighting(hover dim),
                     ChartDataLabelPlugin(막대 위 수치)
                사유: recharts/PivotChartComposed에 대응 훅이 없고, 1차 목표(조회·표시)에 불필요 — 가이드 §A-7에서 Tier2 확정 */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
              customTooltip={AbsValueTooltip}
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ResrcWipProfile);
